import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { TimeSheetDetails } from "@/v2/api/time-sheet/FindOneTimeSheetLog";
import SaveTimeSheet from "@/v2/api/time-sheet/SaveTimeSheet";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";
import { MouseEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const statusConfig: Record<
    "APPROVED" | "TO_BE_VERIFIED" | "VERIFIED" | "REJECTED",
    {
        buttonLabel: string;
        title: string;
        description: string;
        confirmLabel: string;
    }
> = {
    APPROVED: {
        buttonLabel: "Approve",
        title: "Approve this timesheet?",
        description:
            "This action will mark the timesheet as approved. Please ensure all hours and deductions are correct before continuing.",
        confirmLabel: "Approve",
    },

    TO_BE_VERIFIED: {
        buttonLabel: "Send for Verification",
        title: "Send this timesheet for verification?",
        description:
            "This action will move the timesheet to verification review. Make sure all entries are accurate before proceeding.",
        confirmLabel: "Send",
    },

    VERIFIED: {
        buttonLabel: "Verify",
        title: "Verify this timesheet?",
        description:
            "This action will mark the timesheet as verified. Please confirm all information is correct before continuing.",
        confirmLabel: "Verify",
    },

    REJECTED: {
        buttonLabel: "Reject",
        title: "Reject this timesheet?",
        description:
            "This action will mark the timesheet as rejected. Please review the entries carefully before proceeding.",
        confirmLabel: "Reject",
    },
};

export default function TimeSheetStatusActionBtn({
    timeSheetId,
    timeSheetDetails,
    getDeductionData,
    status,
}: {
    timeSheetId: string;
    timeSheetDetails: TimeSheetDetails;
    status: "APPROVED" | "TO_BE_VERIFIED" | "VERIFIED" | "REJECTED";
    getDeductionData: () => {
        other_deductions: number;
        deduction_note: string;
    };
}) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const currentStatusConfig = statusConfig[status];

    const month = useMemo(() => {
        return format(parseISO(timeSheetDetails.start_date), "yyyy-MM");
    }, [timeSheetDetails]);

    const { mutate, isPending } = useMutation({
        mutationFn: SaveTimeSheet,
        onSuccess: () => {
            toast.success(`Status updated successfully`);
            queryClient.invalidateQueries({
                queryKey: ["time-sheets"],
            });
        },

        onSettled: () => {
            setOpen(false);
            if (status === "APPROVED") {
                navigate(`/time-sheet/approved?month=${month}`);
            } else if (status === "VERIFIED") {
                navigate(`/time-sheet/verified?month=${month}`);
            } else if (status === "TO_BE_VERIFIED") {
                navigate(`/time-sheet/to-be-verified?month=${month}`);
            }
        },
    });

    function handleSubmit(e: MouseEvent<HTMLButtonElement>) {
        e.preventDefault();

        const latestDeductions = getDeductionData();

        const payload = {
            data: {
                title: timeSheetDetails.title,
                description: timeSheetDetails.description,
                status: status,
                note: timeSheetDetails.note,
                other_deductions:
                    latestDeductions.other_deductions ??
                    timeSheetDetails.other_deductions,
                deduction_note:
                    latestDeductions.deduction_note ??
                    timeSheetDetails.deduction_note,
            },
            timeSheetId: timeSheetId,
        };
        mutate(payload);
    }

    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
                <Button
                    size="sm"
                    className={`${status === "REJECTED" ? "bg-red-800 hover:bg-red-600" : ""} w-full`}
                >
                    {currentStatusConfig.buttonLabel}
                </Button>
            </AlertDialogTrigger>

            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {currentStatusConfig.title}
                    </AlertDialogTitle>

                    <AlertDialogDescription>
                        {currentStatusConfig.description}
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={isPending}>
                        Cancel
                    </AlertDialogCancel>

                    <AlertDialogAction
                        onClick={handleSubmit}
                        disabled={isPending}
                        className={`${status === "REJECTED" ? "bg-red-800 hover:bg-red-600" : ""}`}
                    >
                        {isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            currentStatusConfig.confirmLabel
                        )}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
